import type { ContactCategory } from './types'

/**
 * Display order for the per-category payload fields. Postgres `jsonb`
 * doesn't preserve insertion order, so without this every detail page
 * would render fields in whatever order the storage engine happened to
 * return them - for bugs that meant Expected and Actual ended up on
 * different rows, breaking the natural side-by-side comparison.
 *
 * Keys not listed here fall to the bottom in raw payload order so a
 * future field shows up rather than disappearing.
 */
export const PAYLOAD_KEY_ORDER: Record<ContactCategory, string[]> = {
  sales:   ['company', 'type', 'teamSize'],
  // For bugs the layout below pairs (severity | browserInfo) on row 1,
  // (pageAffected | stepsToReproduce) on row 2, and (expectedBehavior |
  // actualBehavior) on row 3 - so Expected and Actual sit in the same
  // grid row for direct comparison.
  bug:     ['severity', 'browserInfo', 'pageAffected', 'stepsToReproduce', 'expectedBehavior', 'actualBehavior'],
  feature: ['useCase', 'audience', 'priority'],
  support: ['pageAffected'],
}

export function orderedPayload(
  payload: Record<string, string | undefined> | null | undefined,
  category: ContactCategory,
): Array<[string, string]> {
  if (!payload) return []
  const order = PAYLOAD_KEY_ORDER[category] ?? []
  const seen = new Set<string>()
  const out: Array<[string, string]> = []
  for (const k of order) {
    const v = payload[k]
    if (v) {
      out.push([k, String(v)])
      seen.add(k)
    }
  }
  for (const [k, v] of Object.entries(payload)) {
    if (!seen.has(k) && v) out.push([k, String(v)])
  }
  return out
}
